import {
    calendarPrintSettingsCalendarLabel,
    calendarPrintSettingsViewLabel,
    calendarPrintSettingsLayoutLabel,
    calendarPrintSettingsTimeRangeLabel,
    calendarPrintSettingsTimeRangeText,
    calendarPrintSettingsShowMiniMonth,
    calendarPrintSettingsShowAgenda,
    calendarPrintSettingsShowNotesSection,
} from './CalendarPrintSettings.base.locstring.json';
import { calendarPrintSettingsMultiCalendarsLabel } from 'owa-locstrings/lib/strings/calendarprintsettingsmulticalendarslabel.locstring.json';
import loc from 'owa-localize';
import * as React from 'react';
import { CalendarPrintHourPicker } from '../CalendarPrintHourPicker';
import {
    CalendarPrintSettingsProps,
    CalendarPrintSettingsStyleProps,
    CalendarPrintSettingsStyles,
} from './CalendarPrintSettings.types';
import { classNamesFunction } from '@fluentui/react/lib/Utilities';
import { Dropdown, DropdownMenuItemType, IDropdownOption } from '@fluentui/react/lib/Dropdown';
import { getViews } from './getViews';
import { getWeekLayouts } from './getWeekLayouts';
import { Checkbox } from '@fluentui/react/lib/Checkbox';
import { observer } from 'mobx-react-lite';
import { getOWAConnectedAccounts } from 'owa-accounts-store';
import { getCalendarColorScheme } from 'owa-calendar-color-resolver';
import { ControlIcons } from 'owa-control-icons';
import { Icon } from '@fluentui/react/lib/Icon';
import { DateRangeType } from '..';
import { getSortedCalendarsMap } from '../../utils/calendarGroupUtils';

const getClassNames = classNamesFunction<
    CalendarPrintSettingsStyleProps,
    CalendarPrintSettingsStyles
>();

let cssStyles = require('./CalendarPrintSettings.scss');
let bindClassNames = require('classnames/bind').bind(cssStyles);

export const CalendarPrintSettingsBase = observer(function CalendarPrintSettingsBase(
    props: CalendarPrintSettingsProps
) {
    const hasConnectedAccounts = getOWAConnectedAccounts().length > 0;

    // Styles
    const { styles, className, customStyles } = props;
    const classNames = getClassNames(styles, { className: className });

    // User settings
    const {
        calendarId,
        view,
        weekLayout,
        agenda,
        fromHour,
        toHour,
        miniMonth,
        notes,
        hideWorkWeekView,
    } = props.printSettings;

    // Application settings
    const { calendars, onChange } = props;

    const sortedCalendars = getSortedCalendarsMap(calendars);

    const calendarOptions: IDropdownOption[] = [];

    let calendarAccount: string = null;
    // Calculated properties
    Object.keys(sortedCalendars).map(id => {
        const isNewAccount =
            sortedCalendars[id].calendarId &&
            sortedCalendars[id].calendarId.mailboxInfo.userIdentity !== calendarAccount;
        if (hasConnectedAccounts && isNewAccount) {
            calendarAccount = sortedCalendars[id].calendarId.mailboxInfo.userIdentity;
            calendarOptions.push({
                key: `${calendarAccount}-${sortedCalendars[id].name}-divider`,
                text: '-',
                itemType: DropdownMenuItemType.Divider,
            });
            calendarOptions.push({
                key: `${calendarAccount}-${sortedCalendars[id].name}`,
                text: calendarAccount,
                itemType: DropdownMenuItemType.Header,
            });
        }

        const dropDownData = sortedCalendars[id].calendarId
            ? {
                  iconColor: getCalendarColorScheme(sortedCalendars[id].calendarId).calPrimary,
                  iconName: ControlIcons.CircleFill,
                  accountName: calendarAccount,
              }
            : undefined;

        calendarOptions.push({
            key: id,
            text:
                id != loc(calendarPrintSettingsMultiCalendarsLabel)
                    ? sortedCalendars[id].name.join(', ')
                    : id,
            data: dropDownData,
        });
    });

    // Event Handlers
    const onCalendarChanged = selectedCalendar => onChange({ calendarId: selectedCalendar.key });
    const onViewChanged = selectedView => onChange({ view: selectedView.key });
    const onLayoutChanged = selectedLayout => onChange({ weekLayout: selectedLayout.key });
    const onPrintFromHourChanged = fromHour => onChange({ fromHour });
    const onPrintToHourChanged = toHour => onChange({ toHour });
    const onMinimonthChanged = (e, miniMonth) => onChange({ miniMonth });
    const onAgendaChanged = (e, agenda) => onChange({ agenda });
    const onNotesSectionChanged = (e, notes) => onChange({ notes });

    return (
        <div
            className={bindClassNames(
                classNames.root,
                cssStyles.settingsDropdown,
                cssStyles.settingsLabel
            )}>
            <Dropdown
                key="calendars"
                label={loc(calendarPrintSettingsCalendarLabel)}
                options={calendarOptions}
                selectedKey={calendarId}
                onChanged={onCalendarChanged}
                onRenderOption={onRenderOption}
                styles={{ ...customStyles, dropdownItemHeader: cssStyles.accountHeader }}
            />
            <Dropdown
                key="views"
                label={loc(calendarPrintSettingsViewLabel)}
                options={getViews(hideWorkWeekView)}
                selectedKey={view}
                onChanged={onViewChanged}
                styles={customStyles}
            />
            {(view === DateRangeType.Week || view === DateRangeType.WorkWeek) && !agenda && (
                <Dropdown
                    key="layout"
                    label={loc(calendarPrintSettingsLayoutLabel)}
                    options={getWeekLayouts()}
                    selectedKey={weekLayout}
                    onChanged={onLayoutChanged}
                    styles={customStyles}
                />
            )}
            <div className={cssStyles.timeRange}>
                <div className={cssStyles.timeDropDown}>
                    <CalendarPrintHourPicker
                        key="printFrom"
                        label={loc(calendarPrintSettingsTimeRangeLabel)}
                        min={0}
                        max={toHour - 1}
                        selected={fromHour}
                        onChanged={onPrintFromHourChanged}
                        customStyles={customStyles}
                    />
                </div>
                <span className={cssStyles.textInTimeRange}>
                    {loc(calendarPrintSettingsTimeRangeText)}{' '}
                </span>
                <div className={cssStyles.timeDropDown}>
                    <CalendarPrintHourPicker
                        key="printTo"
                        label=""
                        min={fromHour + 1}
                        max={24}
                        selected={toHour}
                        onChanged={onPrintToHourChanged}
                        customStyles={customStyles}
                    />
                </div>
            </div>
            <Checkbox
                key="calendarZoom"
                label={loc(calendarPrintSettingsShowMiniMonth)}
                checked={miniMonth}
                onChange={onMinimonthChanged}
            />
            <Checkbox
                key="agenda"
                label={loc(calendarPrintSettingsShowAgenda)}
                checked={agenda}
                onChange={onAgendaChanged}
            />
            {view === DateRangeType.Day && !agenda && (
                <Checkbox
                    key="notes"
                    label={loc(calendarPrintSettingsShowNotesSection)}
                    checked={notes}
                    onChange={onNotesSectionChanged}
                />
            )}
        </div>
    );
});

function onRenderOption(
    option: IDropdownOption,
    defaultRender: (option: IDropdownOption) => JSX.Element
): JSX.Element {
    if (!option.data) {
        return defaultRender(option);
    }

    const iconStyles: React.CSSProperties = {
        color: option.data.iconColor,
        fontSize: '8px',
    };
    return createDropdownItem(iconStyles, option.data.iconName, option.text);
}

function createDropdownItem(
    iconStyles: React.CSSProperties,
    iconName: string,
    text: string
): JSX.Element {
    return (
        <div className={cssStyles.calendarSelectionItem}>
            <Icon
                style={iconStyles}
                className={cssStyles.calendarSelectionIcon}
                iconName={iconName}
            />
            <div className={cssStyles.calendarSelectionText}>{text}</div>
        </div>
    );
}
